/**
 * IT Email Form - Public page for IT team to submit work email
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  CssBaseline,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useParams } from 'react-router-dom';
import { itEmailService, WorkEmailFormData } from '../services/itEmailService';
import api from '../services/api';

const ITEmailForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  // Detect system theme preference
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  // Create theme based on system preference
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: prefersDarkMode ? 'dark' : 'light',
        },
      }),
    [prefersDarkMode]
  );

  const [formData, setFormData] = useState<WorkEmailFormData | null>(null);
  const [workEmail, setWorkEmail] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [itEmail, setItEmail] = useState('');
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Field-specific validation errors
  const [workEmailError, setWorkEmailError] = useState<string>('');
  const [itEmailError, setItEmailError] = useState<string>('');
  const [checkingEmail, setCheckingEmail] = useState<boolean>(false);

  // Checklist state
  const [checklistState, setChecklistState] = useState({
    work_email_created: false,
    outlook_configured: false,
    teams_configured: false,
    laptop_assigned: false,
    wifi_configured: false,
  });

  // Custom checklist items state
  const [customItems, setCustomItems] = useState<Array<{name: string; completed: boolean}>>([]);

  useEffect(() => {
    const loadFormData = async () => {
      if (!token) {
        setError('Invalid link - no token provided');
        setLoading(false);
        return;
      }

      try {
        const data = await itEmailService.getFormData(token);
        setFormData(data);
        if (data.work_email) {
          setWorkEmail(data.work_email);
        }
        // Initialize checklist state from form data
        setChecklistState({
          work_email_created: data.work_email_created || false,
          outlook_configured: data.outlook_configured || false,
          teams_configured: data.teams_configured || false,
          laptop_assigned: data.laptop_assigned || false,
          wifi_configured: data.wifi_configured || false,
        });

        // Parse custom checklist items
        if (data.custom_checklist_items) {
          try {
            const parsed = JSON.parse(data.custom_checklist_items);
            setCustomItems(Array.isArray(parsed) ? parsed : []);
          } catch (e) {
            console.error('Failed to parse custom checklist items:', e);
            setCustomItems([]);
          }
        } else {
          setCustomItems([]);
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || 'Failed to load form data. The link may be invalid or expired.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [token]);

  // Email validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateWorkEmail = (email: string): string => {
    if (!email) {
      return 'Work email is required';
    }
    if (!validateEmail(email)) {
      return 'Please enter a valid email address';
    }
    if (!email.endsWith('@datafactz.ai')) {
      return 'Work email must end with @datafactz.ai';
    }
    return '';
  };

  const validateItEmail = (email: string): string => {
    if (!email) {
      return 'IT email is required';
    }
    if (!validateEmail(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  // Check if email already exists in the database
  const checkEmailExists = async (email: string) => {
    if (!email || !email.endsWith('@datafactz.ai')) return;

    setCheckingEmail(true);
    try {
      const response = await api.get(`/employees/check-email?email=${encodeURIComponent(email)}`);

      if (response.data.exists) {
        setWorkEmailError(`⚠️ ${response.data.message}`);
      }
    } catch (error: any) {
      console.error('Error checking email:', error);
      // Don't set error for API failures, just log it
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleWorkEmailChange = (value: string) => {
    setWorkEmail(value);
    if (value) {
      const error = validateWorkEmail(value);
      setWorkEmailError(error);
    } else {
      setWorkEmailError('');
    }
  };

  // Debounced email existence check
  useEffect(() => {
    if (!workEmail || workEmailError) return;

    const timeoutId = setTimeout(() => {
      checkEmailExists(workEmail);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [workEmail]);

  const handleItEmailChange = (value: string) => {
    setItEmail(value);
    if (value) {
      const error = validateItEmail(value);
      setItEmailError(error);
    } else {
      setItEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !workEmail || !temporaryPassword || !itEmail) return;

    // Validate before submit
    const workEmailValidationError = validateWorkEmail(workEmail);
    const itEmailValidationError = validateItEmail(itEmail);

    if (workEmailValidationError) {
      setWorkEmailError(workEmailValidationError);
      return;
    }

    if (itEmailValidationError) {
      setItEmailError(itEmailValidationError);
      return;
    }

    setSubmitting(true);
    setError(null);
    setWorkEmailError('');
    setItEmailError('');

    try {
      await itEmailService.submitWorkEmail(token, workEmail, temporaryPassword, itEmail, {
        work_email_created: checklistState.work_email_created,
        outlook_configured: checklistState.outlook_configured,
        teams_configured: checklistState.teams_configured,
        laptop_assigned: checklistState.laptop_assigned,
        wifi_configured: checklistState.wifi_configured,
        custom_checklist_items: customItems.length > 0 ? JSON.stringify(customItems) : undefined,
      });
      setSuccess(true);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to submit work email';

      // Check if it's a validation error for specific fields
      if (errorMessage.toLowerCase().includes('work email') && errorMessage.toLowerCase().includes('@datafactz.ai')) {
        setWorkEmailError(errorMessage);
      } else if (errorMessage.toLowerCase().includes('it email')) {
        setItEmailError(errorMessage);
      } else {
        setError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // Success state
  if (success) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            py: 4,
          }}
        >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#F97316',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Card
              sx={{
                width: '100%',
                maxWidth: 480,
                borderRadius: '20px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                backgroundColor: prefersDarkMode ? 'rgba(30, 41, 59, 0.75)' : 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: prefersDarkMode ? '1px solid rgba(51, 65, 85, 0.5)' : '1px solid rgba(255, 255, 255, 0.5)',
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: '#F97316', mb: 2 }}
                >
                  IT Setup Completed Successfully!
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  The work email, temporary password, and checklist have been saved.
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  A welcome email with login credentials has been sent to the employee's personal email.
                </Typography>
                <Typography color="text.secondary">
                  HR and Admin have also been notified with the updated checklist status.
                </Typography>
                <Typography variant="body2" sx={{ mt: 3, color: 'text.disabled' }}>
                  You can close this window.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
      </ThemeProvider>
    );
  }

  // Error state (when form data couldn't be loaded)
  if (!formData) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            py: 4,
          }}
        >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Card
            sx={{
              width: '100%',
              maxWidth: 480,
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
              backgroundColor: prefersDarkMode ? 'rgba(30, 41, 59, 0.75)' : 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: prefersDarkMode ? '1px solid rgba(51, 65, 85, 0.5)' : '1px solid rgba(255, 255, 255, 0.5)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Alert severity="error" sx={{ borderRadius: '12px' }}>
                {error || 'Unable to load the form. The link may be invalid or expired.'}
              </Alert>
            </CardContent>
          </Card>
        </Container>
      </Box>
      </ThemeProvider>
    );
  }

  // Form state
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          // Clean background with subtle warmth
          background: prefersDarkMode ? `
            linear-gradient(135deg, #1E293B 0%, #0F172A 25%, #020617 50%, #0F172A 75%, #1E293B 100%)
          ` : `
            linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 25%, #F5F5F5 50%, #FAFAFA 75%, #FFFFFF 100%)
          `,
          overflow: 'hidden',
          py: 4,
        }}
      >
        {/* Large Grid Background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(rgba(249, 115, 22, 0.06) 1px, transparent 1px),
              linear-gradient(90deg, rgba(249, 115, 22, 0.06) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Top Right */}
        <Box
          sx={{
            position: 'absolute',
            top: '-25%',
            right: '-15%',
            width: '700px',
            height: '700px',
            background: 'radial-gradient(circle, rgba(249, 115, 22, 0.12) 0%, rgba(251, 146, 60, 0.04) 35%, transparent 65%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Bottom Left */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '-20%',
            left: '-10%',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(234, 88, 12, 0.08) 0%, rgba(249, 115, 22, 0.03) 40%, transparent 65%)',
            borderRadius: '50%',
            filter: 'blur(70px)',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Center (behind card) */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(249, 115, 22, 0.1) 0%, rgba(251, 146, 60, 0.04) 40%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Top Left (subtle) */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '-5%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(251, 146, 60, 0.06) 0%, transparent 60%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
            zIndex: 0,
          }}
        />

        {/* Accent Line - Top */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, transparent 0%, #F97316 50%, transparent 100%)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Logo/Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '16px',
              bgcolor: '#F97316',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <EmailIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>

          {/* Form Card - Glassmorphic */}
          <Card
            sx={{
              width: '100%',
              maxWidth: 480,
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
              backgroundColor: prefersDarkMode ? 'rgba(30, 41, 59, 0.75)' : 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: prefersDarkMode ? '1px solid rgba(51, 65, 85, 0.5)' : '1px solid rgba(255, 255, 255, 0.5)',
              overflow: 'visible',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                component="h1"
                variant="h1"
                align="center"
                sx={{
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  mb: 1,
                  color: 'text.primary',
                }}
              >
                IT Setup Form
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mb: 4 }}
              >
                Complete work email creation and IT checklist for the new employee
              </Typography>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    '& .MuiAlert-message': {
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                {/* First Name (read-only) */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.75,
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    First Name
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.first_name || ''}
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'text.primary',
                      },
                    }}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        fontSize: '0.875rem',
                        bgcolor: 'action.hover',
                      },
                    }}
                  />
                </Box>

                {/* Middle Name (read-only) - only show if present */}
                {formData.middle_name && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 0.75,
                        fontWeight: 500,
                        color: 'text.primary',
                        fontSize: '0.875rem',
                      }}
                    >
                      Middle Name
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.middle_name}
                      disabled
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: 'text.primary',
                        },
                      }}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          fontSize: '0.875rem',
                          bgcolor: 'action.hover',
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Last Name (read-only) */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.75,
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    Last Name
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.last_name || ''}
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'text.primary',
                      },
                    }}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        fontSize: '0.875rem',
                        bgcolor: 'action.hover',
                      },
                    }}
                  />
                </Box>

                {/* Personal Email (read-only) */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.75,
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    Personal Email
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.personal_email || 'N/A'}
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'text.primary',
                      },
                    }}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        fontSize: '0.875rem',
                        bgcolor: 'action.hover',
                      },
                    }}
                  />
                </Box>

                {/* IT Email (input) */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.75,
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    Your IT Email *
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    type="email"
                    value={itEmail}
                    onChange={(e) => handleItEmailChange(e.target.value)}
                    disabled={submitting}
                    placeholder="it.support@datafactz.com"
                    error={!!itEmailError}
                    helperText={itEmailError || "Enter your IT email address (this will be used as the sender)"}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                    }}
                    InputProps={{
                      sx: {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Box>

                {/* HR Notes / Access Requirements (read-only) - only show if present */}
                {formData.comments && (
                  <Box
                    sx={(theme) => ({
                      mb: 2.5,
                      p: 2,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : '#fff3e0',
                      borderLeft: '4px solid #ff9800',
                      borderRadius: '4px',
                    })}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: 'warning.main',
                        fontSize: '0.875rem',
                        mb: 1,
                      }}
                    >
                      HR Notes / Access Requirements
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {formData.comments}
                    </Typography>
                  </Box>
                )}

                {/* IT Checklist Section */}
                <Box
                  sx={(theme) => ({
                    mb: 3,
                    p: 2.5,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(249, 115, 22, 0.15)' : '#FFF7ED',
                    borderLeft: '4px solid #F97316',
                    borderRadius: '8px',
                  })}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#F97316',
                      fontSize: '0.875rem',
                      mb: 1.5,
                    }}
                  >
                    IT Setup Checklist
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme => theme.palette.mode === 'dark' ? '#FB923C' : '#EA580C',
                      fontSize: '0.75rem',
                      mb: 2,
                      display: 'block',
                    }}
                  >
                    Please mark the items you have completed for this employee
                  </Typography>

                  {/* Dynamically render only HR-selected checklist items */}
                  {(() => {
                    // Define all checklist items
                    const allChecklistItems = [
                      {
                        key: 'work_email_created' as const,
                        requiredKey: 'work_email_required' as const,
                        label: 'Work Email Created',
                        description: 'Company email account has been created',
                      },
                      {
                        key: 'outlook_configured' as const,
                        requiredKey: 'outlook_required' as const,
                        label: 'Outlook Configured',
                        description: 'Outlook email client has been set up',
                      },
                      {
                        key: 'teams_configured' as const,
                        requiredKey: 'teams_required' as const,
                        label: 'Microsoft Teams Configured',
                        description: 'Teams account and access has been set up',
                      },
                      {
                        key: 'laptop_assigned' as const,
                        requiredKey: 'laptop_required' as const,
                        label: 'Laptop Assigned',
                        description: 'Company laptop has been provided',
                      },
                      {
                        key: 'wifi_configured' as const,
                        requiredKey: 'wifi_required' as const,
                        label: 'WiFi Access Configured',
                        description: 'Company WiFi credentials provided',
                      },
                    ];

                    // Filter to only show required items
                    const checklistItems = allChecklistItems.filter(
                      (item) => formData?.[item.requiredKey]
                    );

                    // Render filtered items
                    return checklistItems.map((item) => (
                      <Box sx={{ mb: 1.5 }} key={item.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checklistState[item.key]}
                              onChange={(e) =>
                                setChecklistState({
                                  ...checklistState,
                                  [item.key]: e.target.checked,
                                })
                              }
                              disabled={submitting}
                              sx={{
                                color: '#F97316',
                                '&.Mui-checked': {
                                  color: '#F97316',
                                },
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem', color: 'text.primary' }}>
                                {item.label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {item.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    ));
                  })()}

                  {/* Custom Checklist Items */}
                  {customItems.length > 0 && customItems.map((customItem, index) => (
                    <Box sx={{ mb: 1.5 }} key={`custom-${index}`}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={customItem.completed}
                            onChange={(e) => {
                              const updatedItems = [...customItems];
                              updatedItems[index] = { ...updatedItems[index], completed: e.target.checked };
                              setCustomItems(updatedItems);
                            }}
                            disabled={submitting}
                            sx={{
                              color: '#F97316',
                              '&.Mui-checked': {
                                color: '#F97316',
                              },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem', color: 'text.primary' }}>
                              {customItem.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              Custom access requirement
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </Box>

                {/* Work Email (input) */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.75,
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    Work Email *
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    type="email"
                    value={workEmail}
                    onChange={(e) => handleWorkEmailChange(e.target.value)}
                    disabled={submitting}
                    placeholder="employee@datafactz.ai"
                    error={!!workEmailError}
                    helperText={
                      workEmailError
                        ? workEmailError
                        : checkingEmail
                        ? "Checking if email is available..."
                        : "Enter the company email address created for this employee (must end with @datafactz.ai)"
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                    }}
                    InputProps={{
                      sx: {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Box>

                {/* Temporary Password (input) */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.75,
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    Temporary Password *
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    type={showTempPassword ? 'text' : 'password'}
                    value={temporaryPassword}
                    onChange={(e) => setTemporaryPassword(e.target.value)}
                    disabled={submitting}
                    placeholder="Enter temporary password"
                    helperText="This password will be sent to the employee's personal email"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                    }}
                    InputProps={{
                      sx: {
                        fontSize: '0.875rem',
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowTempPassword(!showTempPassword)}
                            edge="end"
                            size="small"
                            sx={{
                              color: 'text.secondary',
                              opacity: 0.7,
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                opacity: 1,
                                backgroundColor: 'transparent',
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            {showTempPassword ? (
                              <VisibilityOff sx={{ fontSize: 20, transition: 'transform 0.2s ease' }} />
                            ) : (
                              <Visibility sx={{ fontSize: 20, transition: 'transform 0.2s ease' }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={submitting || checkingEmail || !workEmail || !temporaryPassword || !itEmail || !!workEmailError || !!itEmailError}
                  sx={{
                    height: '48px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #FB923C 0%, #F97316 100%)',
                    boxShadow: '0 2px 8px -2px rgba(249, 115, 22, 0.3)',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                      boxShadow: '0 4px 12px -2px rgba(249, 115, 22, 0.4)',
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                    '&.Mui-disabled': {
                      background: 'linear-gradient(135deg, #FDBA74 0%, #FB923C 100%)',
                      color: 'rgba(255, 255, 255, 0.8)',
                    },
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Complete IT Setup & Submit'
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Footer */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 4, textAlign: 'center' }}
          >
            HR Management System - IT Setup & Onboarding
          </Typography>
        </Box>
      </Container>
    </Box>
    </ThemeProvider>
  );
};

export default ITEmailForm;
